import { clampDescription } from "@/lib/seo-quality";
import { robotsMeta } from "@/lib/seo-quality";
import { assessBrand } from "@/lib/seo-families/brands";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Suspense } from "react";
import { Phone, ArrowRight, CheckCircle2, MapPin, Calendar } from "lucide-react";
import { PageHeader } from "./fordon";
import { FyndanuGridSkeleton } from "@/components/FyndanuGrid";
import { VehicleBrowser } from "@/components/VehicleBrowser";
import { BrandStockInline } from "@/components/BrandStockInline";
import { BrandModelSeries } from "@/components/BrandModelSeries";
import { fyndanuListingsQuery } from "@/lib/vehicles";
import { HUSVAGN_BRANDS, getBrand } from "@/data/vehicle-brands";
import { getBrandDjup } from "@/data/vehicle-brands-djup";

export const Route = createFileRoute("/husvagnar/$brand")({
  codeSplitGroupings: [["loader", "component"]],
  loader: async ({ context, params }) => {
    const brand = getBrand("husvagn", params.brand.toLowerCase());
    if (!brand) throw notFound();
    await context.queryClient.ensureQueryData(fyndanuListingsQuery);
    // Varumärkesdatan skickas via loadern så head() inte drar in den i bundlen.
    return {
      brand,
      djup: getBrandDjup("husvagn", brand.slug) ?? null,
      robots: robotsMeta(assessBrand(brand)),
    };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const b = loaderData.brand;
    const djup = loaderData.djup;
    const allFaq = [...b.faq, ...(djup?.faq ?? [])];
    const url = `https://www.akhusbilfritid.se/husvagnar/${b.slug}`;
    const title = `${b.name} husvagn till salu – AK Husbil & Fritid, Kalix`;
    const desc = `${b.name} husvagnar hos AK Husbil & Fritid. ${b.tagline}. Fukttestade, prissatta rakt & med transport i hela Sverige.`;
    return {
      meta: [
        ...loaderData.robots,
        { title },
        { name: "description", content: clampDescription(desc) },
        { name: "keywords", content: `${b.name} husvagn, ${b.name} till salu, begagnad ${b.name}, ${b.name} husvagn Sverige` },
        { property: "og:type", content: "website" },
        { property: "og:title", content: title },
        { property: "og:description", content: clampDescription(desc) },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: allFaq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se" },
              { "@type": "ListItem", position: 2, name: "Husvagnar", item: "https://www.akhusbilfritid.se/husvagnar-till-salu" },
              { "@type": "ListItem", position: 3, name: b.name, item: url },
            ],
          }),
        },

      ],
    };
  },
  component: BrandPage,
  notFoundComponent: () => (
    <div className="container-edge py-20">
      <h1 className="font-display text-3xl">Märket hittades inte</h1>
      <ul className="mt-4 flex flex-wrap gap-2">
        {HUSVAGN_BRANDS.map((b) => (
          <li key={b.slug}>
            <Link to="/husvagnar/$brand" params={{ brand: b.slug }} className="rounded-full border border-border bg-card px-4 py-2 text-sm hover:bg-secondary">
              {b.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  ),
});

function BrandPage() {
  const { brand: slug } = Route.useParams();
  const brand = getBrand("husvagn", slug.toLowerCase())!;
  const djup = getBrandDjup("husvagn", brand.slug);
  const allFaq = [...brand.faq, ...(djup?.faq ?? [])];

  return (
    <>
      <PageHeader
        label={`Husvagnar · ${brand.origin} sedan ${brand.founded}`}
        title={<>{brand.name} husvagnar <span className="font-italic-display text-accent">i lager</span>.</>}
        sub={brand.tagline}
      />

      <section className="container-edge -mt-6 pb-4">
        <div className="flex flex-wrap gap-3">
          <a href="tel:0708629743" className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
            <Phone size={16} /> Ring om {brand.name}
          </a>
          <Link to="/husvagnar-till-salu" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Alla husvagnar <ArrowRight size={14} />
          </Link>
          <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Byt in mot {brand.name} <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <section className="container-edge py-10">
        <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
          <div>
            <p className="text-lg leading-relaxed text-muted-foreground">{brand.intro}</p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {brand.strengths.map((s) => (
                <div key={s} className="flex items-start gap-2 rounded-2xl border border-border bg-card p-4 text-sm">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-accent" />
                  <span>{s}</span>
                </div>
              ))}
            </div>
          </div>
          <aside className="rounded-3xl border border-border bg-card p-6">
            <h3 className="font-display text-xl">Fakta om {brand.name}</h3>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex items-start gap-2"><MapPin size={14} className="mt-1 text-accent" /><span><dt className="inline text-muted-foreground">Ursprung: </dt><dd className="inline font-medium">{brand.origin}</dd></span></div>
              <div className="flex items-start gap-2"><Calendar size={14} className="mt-1 text-accent" /><span><dt className="inline text-muted-foreground">Grundat: </dt><dd className="inline font-medium">{brand.founded}</dd></span></div>
            </dl>
            {brand.models.length > 0 && (
              <>
                <h4 className="mt-6 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Populära modeller</h4>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {brand.models.map((m) => (
                    <li key={m} className="rounded-full bg-secondary px-3 py-1 text-xs font-medium">{m}</li>
                  ))}
                </ul>
              </>
            )}
          </aside>
        </div>
      </section>

      <section className="container-edge py-8">
        <h2 className="font-display text-3xl md:text-4xl">{brand.name} husvagnar i lager just nu</h2>
        <p className="mt-2 text-muted-foreground">Filtrera på pris, årsmodell och antal bäddar. Uppdateras dagligen.</p>
        <div className="mt-6">
          <Suspense fallback={<FyndanuGridSkeleton />}>
            <VehicleBrowser
              category="husvagn"
              lockedBrand={brand.name}
              emptyLabel={`Ingen ${brand.name} i lager just nu — ring 070-862 97 43 så letar vi. Vi tar in ${brand.name} kontinuerligt.`}
            />
          </Suspense>
        </div>
      </section>

      <section className="container-edge py-8">
        <BrandStockInline brandSlug={brand.slug} />
      </section>

      <section className="container-edge py-8">
        <BrandModelSeries brandSlug={brand.slug} category="husvagn" />
      </section>

      {djup && (
        <section className="container-edge py-16">
          <p className="eyebrow">Fördjupning</p>
          <h2 className="mt-3 font-display text-3xl md:text-5xl">Allt du behöver veta om {brand.name}</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <div className="rounded-3xl border border-border bg-card p-6">
              <h3 className="font-display text-lg">Historik</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{djup.historik}</p>
            </div>
            <div className="rounded-3xl border border-border bg-card p-6">
              <h3 className="font-display text-lg">Byggteknik</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{djup.byggteknik}</p>
            </div>
            <div className="rounded-3xl border border-border bg-card p-6">
              <h3 className="font-display text-lg">Prisbild</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{djup.prisbild}</p>
            </div>
            <div className="rounded-3xl border border-border bg-card p-6">
              <h3 className="font-display text-lg">Värdeminskning</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{djup.vardeminskning}</p>
            </div>
            <div className="rounded-3xl border border-border bg-card p-6">
              <h3 className="font-display text-lg">Vinterduglighet</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{djup.vinterduglighet}</p>
            </div>
            <div className="rounded-3xl border border-border bg-card p-6">
              <h3 className="font-display text-lg">Reservdelar</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{djup.reservdelar}</p>
            </div>
            <div className="rounded-3xl border border-border bg-card p-6 md:col-span-2">
              <h3 className="font-display text-lg">Vanliga fel</h3>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                {djup.vanligaFel.map((fel) => (
                  <li key={fel} className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-accent" />
                    <span>{fel}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl border border-border bg-card p-6 md:col-span-2">
              <h3 className="font-display text-lg">{brand.name} på svenska marknaden</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{djup.sverigemarknad}</p>
            </div>
          </div>
        </section>
      )}

      {allFaq.length > 0 && (
        <section className="container-edge py-16">
          <p className="eyebrow">Vanliga frågor</p>
          <h2 className="mt-3 font-display text-3xl md:text-5xl">Frågor om {brand.name}</h2>
          <div className="mt-8 divide-y divide-border rounded-3xl border border-border bg-card">
            {allFaq.map((f, i) => (
              <details key={i} className="group p-6">
                <summary className="cursor-pointer list-none font-display text-lg">{f.q}</summary>
                <p className="mt-3 text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      <section className="bg-secondary py-14">
        <div className="container-edge">
          <p className="eyebrow">Se fler märken</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {HUSVAGN_BRANDS.filter((b) => b.slug !== brand.slug).map((b) => (
              <Link key={b.slug} to="/husvagnar/$brand" params={{ brand: b.slug }} className="rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-background">
                {b.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
